/**
 * SettingsTab — Account, Organization Profile, and Data Corrections
 *
 * F0.2: Legal Identity (L0 read-only), Org Details (L1 summary), Profile Completeness
 * Also: CRA data corrections for matching measures M7/M8
 */
import { useState } from "react";
import {
  Settings, User, Building2, AlertTriangle, CheckCircle2,
  Pencil, X, RotateCcw, Loader2, Save, Info, TrendingUp,
  Lock, Globe, MapPin, ArrowRight,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useOrgProfile, useUpdateOrgProfile, useDeleteOverride, useOrgPositioning } from "../../hooks/useProfile";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import apiClient from "../../lib/apiClient";

// ── Province list ─────────────────────────────────────────────────────────────
const PROVINCES = [
  "AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR","GU","VI","AS","MP",
];

// ── Field metadata for display ────────────────────────────────────────────────
const FIELD_META = {
  revenue_total:     { label: "Total Revenue",        fmt: "dollar",   matching: "M3 Survey · M7 Efficiency" },
  expenditure_total: { label: "Total Expenditures",   fmt: "dollar",   matching: "M7 Efficiency" },
  assets_total:      { label: "Total Assets",         fmt: "dollar",   matching: "M7 Efficiency" },
  years_operating:   { label: "Years Operating",      fmt: "years",    matching: "M8 Assurance · M14 Establishment" },
  fiscal_year:       { label: "Fiscal Year",          fmt: "year",     matching: "Data freshness" },
  city:              { label: "City",                 fmt: "text",     matching: "M1 Geographic scope" },
  province:          { label: "Province",             fmt: "province", matching: "M1 Geographic scope" },
  f7_track_record:   { label: "Track Record Score",   fmt: "score01",  matching: "M8 Assurance",
    help: "Normalized 0–1 score derived from CRA filing history. Override if your organization has a stronger track record than the CRA data reflects." },
};

function fmt(val, type) {
  if (val === null || val === undefined) return "—";
  if (type === "dollar") {
    if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
    if (val >= 1_000)    return `$${(val / 1_000).toFixed(0)}K`;
    return `$${val}`;
  }
  if (type === "score01") return `${(val * 100).toFixed(1)}%`;
  if (type === "years") return `${val} yrs`;
  return String(val);
}

// ── Single editable field row ─────────────────────────────────────────────────
function DataField({ fieldKey, meta, source, override, onSave, onRevert, saving, labelOverride, regionOptions, regionPlaceholder }) {
  const [editing, setEditing] = useState(false);
  const [rawValue, setRawValue] = useState("");
  const [note, setNote]         = useState("");

  const hasOverride  = !!override;
  const displayVal   = hasOverride
    ? _castDisplay(override.corrected_value, meta.fmt)
    : (source?.[fieldKey] ?? null);

  function _castDisplay(strVal, fmtType) {
    if (!strVal) return null;
    const n = parseFloat(strVal);
    return isNaN(n) ? strVal : n;
  }

  const startEdit = () => {
    setRawValue(hasOverride ? override.corrected_value : (source?.[fieldKey] ?? ""));
    setNote(hasOverride ? (override.correction_note || "") : "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    await onSave(fieldKey, rawValue, note);
    setEditing(false);
  };

  return (
    <div className={`rounded-lg border p-4 transition-colors ${hasOverride ? "border-amber-200 bg-amber-50" : "border-zinc-100 bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-zinc-900">{labelOverride || meta.label}</span>
            {hasOverride && (
              <Badge className="text-xs bg-amber-100 text-amber-700 border-0">Corrected</Badge>
            )}
          </div>

          {/* Value display */}
          {!editing && (
            <div className="space-y-1">
              <div className="flex items-baseline gap-3">
                <span className={`text-base font-semibold ${hasOverride ? "text-amber-800" : "text-zinc-800"}`}>
                  {fmt(displayVal, meta.fmt)}
                </span>
                {hasOverride && source?.[fieldKey] != null && (
                  <span className="text-xs text-zinc-400 line-through">
                    CRA: {fmt(source[fieldKey], meta.fmt)}
                  </span>
                )}
              </div>
              {hasOverride && override.correction_note && (
                <p className="text-xs text-amber-700 italic">
                  Note: {override.correction_note}
                </p>
              )}
              <p className="text-xs text-zinc-400">Affects: {meta.matching}</p>
              {meta.help && (
                <p className="text-xs text-zinc-400 mt-0.5 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  {meta.help}
                </p>
              )}
            </div>
          )}

          {/* Edit form */}
          {editing && (
            <div className="space-y-2 mt-2">
              {meta.fmt === "province" ? (
                <select
                  value={rawValue}
                  onChange={(e) => setRawValue(e.target.value)}
                  className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">{regionPlaceholder || "Select province…"}</option>
                  {(regionOptions || PROVINCES).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={meta.fmt === "text" ? "text" : "number"}
                  value={rawValue}
                  onChange={(e) => setRawValue(e.target.value)}
                  step={meta.fmt === "score01" ? "0.01" : "1"}
                  min={meta.fmt === "score01" ? "0" : undefined}
                  max={meta.fmt === "score01" ? "1" : undefined}
                  placeholder={meta.fmt === "score01" ? "0.0 – 1.0" : "Corrected value…"}
                  className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              )}
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reason for correction (e.g. Amended T3010 filed Mar 2025)…"
                className="border border-zinc-200 rounded-lg px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={save}
                  disabled={saving || !rawValue}
                  className="bg-teal-600 hover:bg-teal-700 text-white h-7 px-3 text-xs"
                >
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={cancel} className="h-7 px-2 text-xs">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!editing && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-zinc-400 hover:text-teal-600"
              onClick={startEdit}
              title="Correct this value"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {hasOverride && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-zinc-400 hover:text-red-500"
                onClick={() => onRevert(fieldKey)}
                title="Revert to CRA data"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Account section ───────────────────────────────────────────────────────────
function AccountSection({ user, updateUser }) {
  const [editing, setEditing] = useState(false);
  const [name, setName]       = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const startEdit = () => {
    setName(user?.full_name || "");
    setEditing(true);
    setError(null);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await apiClient.patch("/auth/me", { full_name: name });
      updateUser({ full_name: name });
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-teal-600" />
          <div>
            <CardTitle className="text-base">Account</CardTitle>
            <CardDescription className="text-xs">Manage your login and display name</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-zinc-400 mb-0.5">Email</p>
            <p className="font-medium text-zinc-800">{user?.email || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-400 mb-0.5">Business Number (BN)</p>
            <p className="font-medium text-zinc-800 font-mono text-xs">{user?.charity_bn || "Not set"}</p>
          </div>
        </div>

        {/* Name field */}
        <div>
          <p className="text-xs text-zinc-400 mb-1">Display Name</p>
          {!editing ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-zinc-800">{user?.full_name || "—"}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-400 hover:text-teal-600" onClick={startEdit}>
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save()}
                className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1"
              />
              <Button size="sm" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white h-8">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8">
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main SettingsTab ──────────────────────────────────────────────────────────
export default function SettingsTab() {
  const { user, updateUser } = useAuth();
  const bn = user?.charity_bn;

  const { data: profileData, isLoading, error: profileError } = useOrgProfile(bn);
  const { data: posData, isLoading: posLoading } = useOrgPositioning(bn);
  const updateMutation  = useUpdateOrgProfile(bn);
  const revertMutation  = useDeleteOverride(bn);

  const [savingField, setSavingField] = useState(null);
  const [toast, setToast]             = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (fieldKey, value, note) => {
    setSavingField(fieldKey);
    try {
      await updateMutation.mutateAsync({ [fieldKey]: { value, note: note || null } });
      showToast(`${fieldLabel(fieldKey)} updated. Grant matching will use the corrected value.`);
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to save correction", "error");
    } finally {
      setSavingField(null);
    }
  };

  const handleRevert = async (fieldKey) => {
    try {
      await revertMutation.mutateAsync(fieldKey);
      showToast(`${fieldLabel(fieldKey)} reverted to source data.`);
    } catch (e) {
      showToast("Failed to revert", "error");
    }
  };

  const source    = profileData?.source    || {};
  const overrides = profileData?.overrides || {};
  const effective = profileData?.effective || {};
  const jurisdiction   = profileData?.jurisdiction || "CA";
  const isUS           = jurisdiction === "US";
  const hasAnyOverride = Object.keys(overrides).length > 0;

  // v7: jurisdiction-aware label overrides
  const fieldLabel = (key) => {
    if (key === "province" && isUS) return "State";
    if (key === "province") return "Province";
    return FIELD_META[key]?.label || key;
  };
  const regionOptions = isUS ? US_STATES : PROVINCES;
  const regionPlaceholder = isUS ? "Select state…" : "Select province…";

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-teal-600" />
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Settings</h2>
          <p className="text-sm text-zinc-500">Manage your account and organization data</p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Alert className={`border ${toast.type === "error" ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}`}>
          {toast.type === "error"
            ? <AlertTriangle className="h-4 w-4 text-red-500" />
            : <CheckCircle2 className="h-4 w-4 text-green-500" />
          }
          <AlertDescription className={toast.type === "error" ? "text-red-700" : "text-green-700"}>
            {toast.msg}
          </AlertDescription>
        </Alert>
      )}

      {/* Account Section */}
      <AccountSection user={user} updateUser={updateUser} />

      {/* ── F0.2: Legal Identity + Org Details ─────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-zinc-400" />
              <div>
                <CardTitle className="text-base">Legal Identity</CardTitle>
                <CardDescription className="text-xs">CRA T3010 registered data — read only</CardDescription>
              </div>
            </div>
            {posData && (
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-xs text-zinc-400">Profile completeness</p>
                  <div className="flex items-center gap-1.5">
                    <Progress value={posData.profile_completeness} className="w-20 h-2" />
                    <span className="text-xs font-semibold text-zinc-700">{posData.profile_completeness}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!bn ? (
            <p className="text-sm text-zinc-400">Link your Business Number to view legal identity.</p>
          ) : posLoading ? (
            <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
          ) : posData ? (
            <div className="space-y-4">
              {/* L0 read-only grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {[
                  { label: "Organization",    value: posData.entity_name },
                  { label: "BN / Reg. No.",   value: posData.entity_bn },
                  { label: "Charity Class",   value: posData.l0.charity_classification?.replace(/_/g," ") },
                  { label: "HQ",              value: `${posData.l0.hq_city || "—"}, ${posData.l0.hq_province || "—"}` },
                  { label: "Revenue Band",    value: posData.l0.revenue_band },
                  { label: "Designation Age", value: posData.l0.designation_age_years ? `${posData.l0.designation_age_years} yrs` : "—" },
                  { label: "Fiscal Year",     value: posData.l0.fiscal_year },
                  { label: "Mandate",         value: posData.mandate?.restrictiveness },
                ].map(r => (
                  <div key={r.label} className="flex justify-between text-sm py-1 border-b border-zinc-50">
                    <span className="text-zinc-500">{r.label}</span>
                    <span className="font-medium text-zinc-800 capitalize">{(r.value||"—").toString().toLowerCase().replace(/_/g," ")}</span>
                  </div>
                ))}
              </div>
              {posData.l0.cra_activity_codes?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-400 mb-1.5">CRA Activity Codes</p>
                  <div className="flex flex-wrap gap-1">
                    {posData.l0.cra_activity_codes.map(c => (
                      <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Org Details (L1 summary + link to Positioning tab) */}
      {posData && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-teal-600" />
                <div>
                  <CardTitle className="text-base">Org Details</CardTitle>
                  <CardDescription className="text-xs">Operational details that affect funder eligibility filters</CardDescription>
                </div>
              </div>
              <Badge className={
                posData.profile_completeness >= 80 ? "bg-green-100 text-green-700" :
                posData.profile_completeness >= 50 ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }>
                {posData.profile_completeness >= 80 ? "Complete" :
                 posData.profile_completeness >= 50 ? "Partial" : "Incomplete"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {[
                { label: "Service Geography", value: posData.l1.service_geography_scope },
                { label: "Language",          value: posData.l1.language_of_service },
                { label: "Religious",         value: posData.l1.religious_affiliation },
                { label: "Indigenous Gov.",   value: posData.l1.indigenous_governance },
                { label: "Delivery Model",    value: (posData.l1.program_delivery_model||[]).join(", ") || "—" },
                { label: "Target Populations",value: (posData.l1.target_populations||[]).join(", ") || "—" },
              ].map(r => (
                <div key={r.label} className="flex justify-between text-sm py-1 border-b border-zinc-50 col-span-1">
                  <span className="text-zinc-500 shrink-0">{r.label}</span>
                  <span className="font-medium text-zinc-800 capitalize text-right ml-2 truncate max-w-[160px]">
                    {(r.value||"—").toLowerCase().replace(/_/g," ")}
                  </span>
                </div>
              ))}
            </div>
            <Alert className="border-teal-100 bg-teal-50">
              <MapPin className="h-4 w-4 text-teal-600" />
              <AlertDescription className="text-teal-800 text-xs flex items-center gap-1">
                Edit these details in the <strong>Positioning</strong> tab for full control including target populations and positioning statement.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Org Data Corrections Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              <div>
                <CardTitle className="text-base">Organization Data</CardTitle>
                <CardDescription className="text-xs">
                  CRA T3010 data powers grant matching. If values are incorrect,
                  correct them here — your corrections will be used in all future match runs.
                </CardDescription>
              </div>
            </div>
            {hasAnyOverride && (
              <Badge className="bg-amber-100 text-amber-700 border-0 shrink-0">
                {Object.keys(overrides).length} correction{Object.keys(overrides).length !== 1 ? "s" : ""} active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!bn ? (
            <Alert className="border-zinc-200 bg-zinc-50">
              <AlertDescription className="text-zinc-500 text-sm">
                Complete onboarding and link your Business Number to manage organization data.
              </AlertDescription>
            </Alert>
          ) : isLoading ? (
            <div className="flex items-center gap-2 text-zinc-400 py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading organization data…</span>
            </div>
          ) : profileError ? (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-700 text-sm">
                Could not load organization data. The data may not be available for this BN yet.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {/* Org name header */}
              {effective.org_name && (
                <div className="mb-4 pb-4 border-b border-zinc-100">
                  <p className="text-xs text-zinc-400">Organization</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-semibold text-zinc-900">{effective.org_name}</p>
                    {isUS
                      ? <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">🇺🇸 US Nonprofit</span>
                      : <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">🇨🇦 CA Charity</span>
                    }
                  </div>
                  <p className="text-xs text-zinc-500">
                    {effective.city || "—"}, {effective.province || "—"} · {isUS ? "EIN" : "Designation"}: {effective.designation_code || "—"}
                  </p>
                </div>
              )}

              {/* How corrections affect matching */}
              <Alert className="border-teal-100 bg-teal-50 mb-4">
                <TrendingUp className="h-4 w-4 text-teal-600" />
                <AlertDescription className="text-teal-800 text-xs">
                  Corrections take effect immediately on the next match run. Revenue affects M7 Efficiency.
                  Track record (f7) directly affects M8 Assurance scores.
                </AlertDescription>
              </Alert>

              {/* Correctable fields */}
              <div className="space-y-2">
                {Object.entries(FIELD_META).map(([fieldKey, meta]) => (
                  <DataField
                    key={fieldKey}
                    fieldKey={fieldKey}
                    meta={meta}
                    source={source}
                    override={overrides[fieldKey] || null}
                    onSave={handleSave}
                    onRevert={handleRevert}
                    saving={savingField === fieldKey}
                    labelOverride={fieldLabel(fieldKey)}
                    regionOptions={regionOptions}
                    regionPlaceholder={regionPlaceholder}
                  />
                ))}
              </div>

              {/* Audit note */}
              <p className="text-xs text-zinc-400 mt-4 pt-3 border-t border-zinc-100">
                Original CRA values are preserved for audit. Corrections apply only to grant matching —
                they do not alter your CRA filings.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
